import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Heart, LayoutGrid, Shield, LogOut, User, StickyNote } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL, BRAND_PINK } from "../lib/accents";
import { avatarUrl } from "../lib/api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const AccessBadge = ({ user }) => {
  if (user?.role === "admin") return (
    <span data-testid="access-badge" className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-[#000000] text-white">
      Admin
    </span>
  );
  if (!user?.access_enabled) return (
    <span data-testid="access-badge" className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-[#DF1283] text-white">
      Access Disabled
    </span>
  );
  const days = user?.days_remaining;
  if (days === null || days === undefined) return (
    <span data-testid="access-badge" className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-[#FC861E] text-white">
      Access Active
    </span>
  );
  const bg = days <= 0 ? "bg-[#DF1283]" : days <= 3 ? "bg-[#DF1283]" : "bg-[#FC861E]";
  const label = days <= 0 ? "Access Expired" : `Access — ${days === 1 ? "1 day left" : `${days} days left`}`;
  return (
    <span data-testid="access-badge" className={`hidden sm:inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] text-white ${bg}`}>
      {label}
    </span>
  );
};

export const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const navLink = (to, label, Icon, testId) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`flex items-center gap-1.5 px-2.5 sm:px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
          active ? "bg-[#000000] text-white" : "text-gray-600 hover:bg-gray-100"
        }`}
      >
        <Icon size={16} className="shrink-0" />
        <span className="hidden md:inline">{label}</span>
      </Link>
    );
  };

  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.name || "";
  const initial = ((user?.first_name || user?.name || user?.email || "?")[0] || "?").toUpperCase();
  const avatarSrc = user?.avatar_path ? avatarUrl(user.id) : null;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink min-w-0">
          <img src={LOGO_URL} alt="UnCloned®" className="h-7 sm:h-8 w-auto" />
          <span className="hidden md:inline text-sm font-bold uppercase tracking-[0.15em] text-gray-400 leading-none pt-0.5">Ads Lookbook</span>
        </Link>
        <nav className="flex items-center gap-0.5 sm:gap-1 shrink-0">
          {navLink("/", "Library", LayoutGrid, "nav-library-link")}
          {navLink("/favorites", "Favorites", Heart, "nav-favorites-link")}
          {navLink("/notes", "Notes", StickyNote, "nav-notes-link")}
          {user?.role === "admin" && navLink("/admin", "Admin", Shield, "nav-admin-link")}
          <AccessBadge user={user} />
          <div className="relative ml-1" ref={menuRef}>
            <button
              data-testid="nav-account-btn"
              onClick={() => setOpen((o) => !o)}
              className="w-9 h-9 rounded-full bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center hover:border-[#DF1283] transition-colors"
              aria-label="Account menu"
            >
              {avatarSrc ? (
                <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="font-display font-black text-sm text-[#4A0A66]">{initial}</span>
              )}
            </button>
            {open && (
              <div data-testid="account-menu" className="absolute right-0 mt-2 w-72 bg-white rounded-xl border border-gray-200 shadow-xl p-3 fade-up">
                <div className="px-3 py-2 border-b border-gray-100">
                  <div className="font-semibold text-sm text-[#000000] truncate">{displayName || "—"}</div>
                  <div className="text-xs text-gray-400 truncate">{user?.email}</div>
                  {user?.expires_at && (
                    <div className="text-[11px] text-gray-500 mt-1.5" data-testid="account-expires-at">
                      Access until <strong className="text-[#000000]">{formatDate(user.expires_at)}</strong>
                      {typeof user.days_remaining === "number" && (
                        <span className={`ml-1 ${user.days_remaining <= 3 ? "text-[#DF1283]" : "text-gray-500"}`}>
                          ({user.days_remaining <= 0 ? "expired" : `${user.days_remaining} days left`})
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <Link
                  to="/account"
                  onClick={() => setOpen(false)}
                  data-testid="nav-account-link"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-[#000000] hover:bg-gray-100 transition-colors"
                >
                  <User size={15} /> My Account
                </Link>
                <button
                  data-testid="nav-logout-btn"
                  onClick={async () => { setOpen(false); await logout(); navigate("/login"); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:text-white transition-colors"
                  onMouseEnter={(e) => (e.currentTarget.style.background = BRAND_PINK)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <LogOut size={15} /> Logout
                </button>
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};
